package com.arenaproplay.admin.pages.analytics

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.*
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.charts.svg.Donut
import com.arenaproplay.admin.ui.components.charts.svg.DonutSlice
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodySRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.displaySmall
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.theme.numMedium
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.setUrlParams
import com.arenaproplay.admin.ui.utils.urlParam
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import com.varabyte.kobweb.compose.ui.toAttrs

// ── Provider palette ──────────────────────────────────────────────────────────
private val revProviderColors = mapOf(
    "mono"       to "#4D8CFF",  // Primary light
    "apple"      to "#8FB8FF",  // Primary lighter
    "google"     to "#00E5A4",  // Mint
    "admin"      to "#E4CD40",  // Yellow (admin grant)
    "admin_grant" to "#E4CD40",
    "admin-grant" to "#E4CD40",
)

private val revStatusColors = mapOf(
    "active"   to "#00E5A4",  // Mint
    "canceled" to "#E4CD40",  // Yellow
    "expired"  to "#FF6B5B",  // Danger
)

// ── Public entry point ────────────────────────────────────────────────────────

/**
 * «Выручка» tab — Night Arena dark-glass edition.
 *
 * Sections:
 *  1. KPI chips (activeNow / activations / deactivations / revenue)
 *  2. Two donuts: По провайдеру · Статусный микс
 *  3. Purchases table with status filter chips + pagination
 *
 * CSV export button is wired up by the parent Analytics page via [onCsvExport].
 * The parent invokes [onCsvExport] when the user clicks the button in the topbar.
 */
@Composable
fun RevenueTabContent(
    period: Period,
    repo: AdminRepository,
    /** Callback so parent can register/deregister the CSV action for this tab. */
    onRegisterCsvAction: (() -> Unit) -> Unit,
) {
    val scope = rememberCoroutineScope()

    // ── KPI state ─────────────────────────────────────────────────────────────
    var kpis by remember { mutableStateOf<SubscriptionKpis?>(null) }
    var kpisError by remember { mutableStateOf<String?>(null) }

    // ── Table state ───────────────────────────────────────────────────────────
    var page by remember { mutableStateOf(urlParam("rpage")?.toIntOrNull()?.coerceAtLeast(0) ?: 0) }
    var pageSize by remember {
        mutableStateOf(urlParam("size")?.toIntOrNull()?.takeIf { it in PAGE_SIZE_OPTIONS } ?: 25)
    }
    var query by remember { mutableStateOf(urlParam("q") ?: "") }
    var statusFilter by remember {
        mutableStateOf(
            SubscriptionStatusFilter.entries.find { it.code == urlParam("status") }
                ?: SubscriptionStatusFilter.ALL
        )
    }
    var tableData by remember { mutableStateOf<PageResult<SubscriptionRow>?>(null) }
    var tableError by remember { mutableStateOf<String?>(null) }
    var exporting by remember { mutableStateOf(false) }

    // Load KPIs whenever period changes
    LaunchedEffect(period) {
        kpis = null
        kpisError = null
        runCatching { repo.getSubscriptionKpis(period) }
            .onSuccess { kpis = it }
            .onFailure { kpisError = it.message ?: "Ошибка загрузки" }
    }

    // Load table data; write-back URL params so refresh/back-button restores the same view
    LaunchedEffect(page, pageSize, query, statusFilter) {
        setUrlParams(
            "rpage"  to page.takeIf { it > 0 }?.toString(),
            "q"      to query.takeIf { it.isNotBlank() },
            "status" to statusFilter.code.takeIf { statusFilter != SubscriptionStatusFilter.ALL },
            "size"   to pageSize.takeIf { it != 25 }?.toString(),
        )
        tableData = null
        tableError = null
        runCatching { repo.getSubscriptions(page, pageSize, query, statusFilter) }
            .onSuccess { tableData = it }
            .onFailure { tableError = it.message ?: "Ошибка загрузки" }
    }

    // Register CSV export with parent — period intentionally absent: getSubscriptions is not period-filtered.
    LaunchedEffect(query, statusFilter) {
        onRegisterCsvAction {
            if (exporting) return@onRegisterCsvAction
            exporting = true
            scope.launch {
                try {
                    val all = fetchAllPages { p, size ->
                        repo.getSubscriptions(p, size, query, statusFilter)
                    }
                    downloadCsv(
                        filename = "subscriptions-${statusFilter.code}.csv",
                        header = listOf(
                            "ID пользователя", "Пользователь", "Email", "Статус",
                            "Активен", "Провайдер", "Куплено", "Период до",
                            "Авто-продление",
                        ),
                        rows = all.map {
                            listOf(
                                it.userId, it.user, it.email, it.status,
                                if (it.active) "да" else "нет",
                                revProviderLabel(it.provider), it.purchasedAt,
                                it.currentPeriodEnd,
                                if (it.cancelAtPeriodEnd) "выкл" else "вкл",
                            )
                        },
                    )
                } finally {
                    exporting = false
                }
            }
        }
    }

    // ── Layout ────────────────────────────────────────────────────────────────
    Column(Modifier.fillMaxWidth()) {

        // 1 — KPI chips
        RevKpiRow(kpis, kpisError)

        VSpace(14)

        // 2 — Donuts row
        // Bug-class 1 fix: minmax(0,1fr) so donut legend text can truncate
        // rather than expand the column and overlap the neighbour donut
        Div(attrs = Modifier.fillMaxWidth().styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0,1fr) minmax(0,1fr)")
                property("gap", "14px")
                property("align-items", "start")
            }.toAttrs()
        ) {
            RevProviderDonut(kpis, kpisError)
            RevStatusDonut(kpis, kpisError)
        }

        VSpace(14)

        // 3 — Purchases table
        RevPurchasesCard(
            data = tableData,
            error = tableError,
            statusFilter = statusFilter,
            onStatusFilter = { statusFilter = it; page = 0 },
            query = query,
            onQuery = { query = it; page = 0 },
            page = page,
            pageSize = pageSize,
            onPrev = { if (page > 0) page-- },
            onNext = {
                val total = tableData?.total ?: 0
                if ((page + 1) * pageSize < total) page++
            },
            onPageSizeChange = { pageSize = it; page = 0 },
        )
    }
}

// ── KPI Row ───────────────────────────────────────────────────────────────────

@Composable
private fun RevKpiRow(kpis: SubscriptionKpis?, error: String?) {
    Row(
        Modifier.fillMaxWidth()
            .styleModifier {
                property("gap", "12px")
                property("flex-wrap", "wrap")
            }
    ) {
        // Chip 1 — Активных сейчас
        GlassCard(modifier = Modifier.styleModifier { property("flex", "1 1 160px") }, padding = 16) {
            SpanText("Активных сейчас", Modifier.eyebrow().color(AppColors.Faint))
            Box(Modifier.height(5.px))
            when {
                error != null -> RevChipError(error)
                kpis == null -> RevChipSkeleton()
                else -> SpanText(
                    formatNumber(kpis.activeNow),
                    Modifier.numMedium().color(AppColors.Ink)
                )
            }
            if (kpis != null) {
                Box(Modifier.height(3.px))
                SpanText("PRO подписок активно", Modifier.styleModifier {
                    property("font-size", "11px")
                    property("color", "rgba(240,244,252,.45)")
                })
            }
        }

        // Chip 2 — Активации
        GlassCard(modifier = Modifier.styleModifier { property("flex", "1 1 160px") }, padding = 16) {
            SpanText("Активации", Modifier.eyebrow().color(AppColors.Faint))
            Box(Modifier.height(5.px))
            when {
                error != null -> RevChipError(error)
                kpis == null -> RevChipSkeleton()
                else -> {
                    Row(Modifier.styleModifier { property("align-items", "baseline"); property("gap", "6px") }) {
                        SpanText(
                            formatNumber(kpis.activations.current.toLong()),
                            Modifier.numMedium().color(AppColors.Ink)
                        )
                        RevDeltaPill(kpis.activations)
                    }
                }
            }
            if (kpis != null) {
                Box(Modifier.height(3.px))
                SpanText("за период", Modifier.styleModifier {
                    property("font-size", "11px")
                    property("color", "rgba(240,244,252,.45)")
                })
            }
        }

        // Chip 3 — Деактивации
        GlassCard(modifier = Modifier.styleModifier { property("flex", "1 1 160px") }, padding = 16) {
            SpanText("Деактивации", Modifier.eyebrow().color(AppColors.Faint))
            Box(Modifier.height(5.px))
            when {
                error != null -> RevChipError(error)
                kpis == null -> RevChipSkeleton()
                else -> {
                    Row(Modifier.styleModifier { property("align-items", "baseline"); property("gap", "6px") }) {
                        SpanText(
                            formatNumber(kpis.deactivations.current.toLong()),
                            Modifier.numMedium().color(AppColors.Ink)
                        )
                        // For deactivations, up is bad — invert pill polarity
                        RevDeltaPill(kpis.deactivations, invertPolarity = true)
                    }
                }
            }
            if (kpis != null) {
                Box(Modifier.height(3.px))
                SpanText("за период", Modifier.styleModifier {
                    property("font-size", "11px")
                    property("color", "rgba(240,244,252,.45)")
                })
            }
        }

        // Chip 4 — Выручка
        GlassCard(modifier = Modifier.styleModifier { property("flex", "1 1 160px") }, padding = 16) {
            SpanText("Выручка", Modifier.eyebrow().color(AppColors.Faint))
            Box(Modifier.height(5.px))
            when {
                error != null -> RevChipError(error)
                kpis == null -> RevChipSkeleton()
                else -> {
                    Row(Modifier.styleModifier { property("align-items", "baseline"); property("gap", "6px") }) {
                        SpanText(
                            "₴ ${formatNumber(kpis.revenue.current)}",
                            Modifier.numMedium().styleModifier { property("color", "#E4CD40") }
                        )
                        RevDeltaPill(kpis.revenue)
                    }
                }
            }
            if (kpis != null) {
                Box(Modifier.height(3.px))
                SpanText(kpis.currency, Modifier.styleModifier {
                    property("font-size", "11px")
                    property("color", "rgba(240,244,252,.45)")
                })
            }
        }
    }
}

@Composable
private fun RevChipSkeleton() {
    Box(
        Modifier.fillMaxWidth().height(24.px)
            .styleModifier {
                property("background", "rgba(255,255,255,.04)")
                property("border-radius", "6px")
            }
    )
}

@Composable
private fun RevChipError(msg: String) {
    SpanText("Ошибка", Modifier.bodySRegular().styleModifier { property("color", "#FF6B5B") })
}

@Composable
private fun RevDeltaPill(delta: KpiDelta, invertPolarity: Boolean = false) {
    val pct = delta.percentDelta ?: return
    val isPositive = if (invertPolarity) pct < 0 else pct >= 0
    val label = if (pct >= 0) "+${pct.format(1)}%" else "${pct.format(1)}%"
    Box(
        Modifier.sBorderRadius(999)
            .styleModifier {
                property("padding", "1px 6px")
                property("background", if (isPositive) "rgba(0,229,164,.15)" else "rgba(255,107,91,.15)")
            }
    ) {
        SpanText(
            label,
            Modifier.styleModifier {
                property("font-size", "11px")
                property("font-weight", "600")
                property("font-family", "'Inter', sans-serif")
                property("color", if (isPositive) "#00E5A4" else "#FF6B5B")
            }
        )
    }
}

// ── Provider Donut ────────────────────────────────────────────────────────────

@Composable
private fun RevProviderDonut(kpis: SubscriptionKpis?, error: String?) {
    GlassCard(padding = 16) {
        SpanText("По провайдеру", Modifier.displaySmall().color(AppColors.Ink))
        Box(Modifier.height(4.px))
        SpanText(
            "Где платят текущие подписчики",
            Modifier.bodySRegular().styleModifier { property("color", "rgba(240,244,252,.45)") }
        )
        Box(Modifier.height(14.px))

        when {
            error != null -> SpanText("Ошибка: $error", Modifier.bodySRegular().color(AppColors.Error))
            kpis == null -> RevDonutSkeleton()
            kpis.byProvider.isEmpty() -> SpanText("Нет данных за период", Modifier.bodySRegular().color(AppColors.Faint))
            else -> {
                val slices = kpis.byProvider.map { share ->
                    DonutSlice(
                        name = revProviderLabel(share.name),
                        value = share.value,
                        colorHex = revProviderColors[share.name.lowercase()] ?: "#6F727A",
                    )
                }
                RevDonutWithLegend(slices)
            }
        }
    }
}

@Composable
private fun RevStatusDonut(kpis: SubscriptionKpis?, error: String?) {
    GlassCard(padding = 16) {
        SpanText("Статусный микс", Modifier.displaySmall().color(AppColors.Ink))
        Box(Modifier.height(4.px))
        SpanText(
            "Все подписки по стадии жизненного цикла",
            Modifier.bodySRegular().styleModifier { property("color", "rgba(240,244,252,.45)") }
        )
        Box(Modifier.height(14.px))

        when {
            error != null -> SpanText("Ошибка: $error", Modifier.bodySRegular().color(AppColors.Error))
            kpis == null -> RevDonutSkeleton()
            kpis.byStatus.isEmpty() -> SpanText("Нет данных за период", Modifier.bodySRegular().color(AppColors.Faint))
            else -> {
                val slices = kpis.byStatus.map { share ->
                    DonutSlice(
                        name = revSubStatusLabel(share.name),
                        value = share.value,
                        colorHex = revStatusColors[share.name.lowercase()] ?: "#6F727A",
                    )
                }
                RevDonutWithLegend(slices)
            }
        }
    }
}

@Composable
private fun RevDonutSkeleton() {
    Row(
        Modifier.fillMaxWidth()
            .styleModifier { property("align-items", "center"); property("gap", "20px") }
    ) {
        Box(
            Modifier.width(120.px).height(120.px).sBorderRadius(999)
                .styleModifier { property("background", "rgba(255,255,255,.04)") }
        )
        Column(Modifier.styleModifier { property("flex", "1"); property("gap", "8px") }) {
            repeat(3) {
                Box(Modifier.fillMaxWidth().height(14.px).sBorderRadius(4)
                    .styleModifier { property("background", "rgba(255,255,255,.04)") })
            }
        }
    }
}

@Composable
private fun RevDonutWithLegend(slices: List<DonutSlice>) {
    Row(
        Modifier.fillMaxWidth()
            .styleModifier { property("align-items", "center"); property("gap", "20px") }
    ) {
        Donut(slices = slices, sizePx = 120, strokeWidth = 14)
        // Bug-class 2 fix: min-width:0 + flex:1 so legend names truncate with
        // ellipsis rather than stretching the row beyond the donut card bounds
        Column(Modifier.styleModifier { property("flex", "1"); property("min-width", "0"); property("gap", "6px") }) {
            slices.forEach { slice ->
                Row(
                    Modifier.fillMaxWidth()
                        .styleModifier { property("align-items", "center"); property("gap", "8px") }
                ) {
                    Box(
                        Modifier.width(8.px).height(8.px).sBorderRadius(2)
                            .styleModifier { property("background", slice.colorHex); property("flex-shrink", "0") }
                    )
                    SpanText(
                        slice.name,
                        Modifier.styleModifier {
                            property("flex", "1")
                            property("font-size", "11.5px")
                            property("color", "rgba(240,244,252,.85)")
                            property("font-family", "'Inter', sans-serif")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        }
                    )
                    SpanText(
                        formatNumber(slice.value),
                        Modifier.styleModifier {
                            property("font-family", "'Unbounded', sans-serif")
                            property("font-variant-numeric", "tabular-nums")
                            property("font-size", "11px")
                            property("font-weight", "600")
                            property("color", "#F0F4FC")
                            property("white-space", "nowrap")
                        }
                    )
                }
            }
        }
    }
}

// ── Purchases table ───────────────────────────────────────────────────────────

@Composable
private fun RevPurchasesCard(
    data: PageResult<SubscriptionRow>?,
    error: String?,
    statusFilter: SubscriptionStatusFilter,
    onStatusFilter: (SubscriptionStatusFilter) -> Unit,
    query: String,
    onQuery: (String) -> Unit,
    page: Int,
    pageSize: Int,
    onPrev: () -> Unit,
    onNext: () -> Unit,
    onPageSizeChange: (Int) -> Unit,
) {
    GlassCard(padding = 0) {
        // ── Header ────────────────────────────────────────────────────────────
        Column(
            Modifier.fillMaxWidth()
                .styleModifier {
                    property("padding", "12px 16px 10px 16px")
                    property("border-bottom", "1px solid rgba(255,255,255,.06)")
                    property("gap", "10px")
                }
        ) {
            Row(
                Modifier.fillMaxWidth()
                    .styleModifier { property("align-items", "center"); property("gap", "12px") }
            ) {
                SpanText(
                    "Покупки",
                    Modifier.displaySmall().color(AppColors.Ink)
                        .styleModifier { property("flex", "1") }
                )
                // Search input
                RevSearchInput(query, onQuery)
            }
            // Status filter chips
            RevStatusFilter(statusFilter, onStatusFilter)
        }

        // ── Table body ────────────────────────────────────────────────────────
        when {
            error != null -> {
                Box(Modifier.fillMaxWidth().styleModifier { property("padding", "16px") }) {
                    SpanText("Ошибка: $error", Modifier.bodySRegular().color(AppColors.Error))
                }
            }
            data == null -> {
                Column(Modifier.fillMaxWidth().styleModifier { property("padding", "8px 0") }) {
                    repeat(5) {
                        Box(
                            Modifier.fillMaxWidth().height(40.px)
                                .styleModifier {
                                    property("background", "rgba(255,255,255,.02)")
                                    property("margin", "0 16px 4px 16px")
                                    property("border-radius", "6px")
                                }
                        )
                    }
                }
            }
            data.items.isEmpty() -> {
                Box(Modifier.fillMaxWidth().styleModifier { property("padding", "24px 16px") }) {
                    SpanText("Нет подписок", Modifier.bodySRegular().color(AppColors.Faint))
                }
            }
            else -> {
                // Column headers
                RevTableHeader()
                data.items.forEach { row ->
                    RevTableRow(row)
                }
            }
        }

        // ── Pagination ────────────────────────────────────────────────────────
        Box(
            Modifier.fillMaxWidth()
                .styleModifier {
                    property("padding", "10px 16px")
                    property("border-top", "1px solid rgba(255,255,255,.06)")
                }
        ) {
            PaginationBar(
                page = page,
                pageSize = pageSize,
                total = data?.total ?: 0,
                onPrev = onPrev,
                onNext = onNext,
                onPageSizeChange = onPageSizeChange,
            )
        }
    }
}

@Composable
private fun RevSearchInput(value: String, onValueChange: (String) -> Unit) {
    SearchInput(
        value = value,
        onValueChange = onValueChange,
        placeholderText = "Поиск пользователя",
        modifier = Modifier.width(220.px),
    )
}

@Composable
private fun RevStatusFilter(
    current: SubscriptionStatusFilter,
    onChange: (SubscriptionStatusFilter) -> Unit,
) {
    Row(
        Modifier.styleModifier {
            property("display", "flex")
            property("gap", "4px")
            property("flex-wrap", "wrap")
        }
    ) {
        listOf(
            SubscriptionStatusFilter.ALL to "Все",
            SubscriptionStatusFilter.ACTIVE to "Активные",
            SubscriptionStatusFilter.CANCELED to "Отменённые",
            SubscriptionStatusFilter.EXPIRED to "Истёкшие",
        ).forEach { (filter, label) ->
            RevStatusChip(
                label = label,
                active = current == filter,
                onClick = { onChange(filter) },
            )
        }
    }
}

@Composable
private fun RevStatusChip(label: String, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .sBorderRadius(999)
            .classNames("na-press")
            .styleModifier {
                property("padding", "4px 12px")
                property("cursor", "pointer")
                property("user-select", "none")
                property("transition", "all 140ms ease")
                property("font-family", "'Inter', sans-serif")
                property("font-size", "11px")
                if (active) {
                    property("background", "rgba(0,97,255,.30)")
                    property("border", "1px solid rgba(77,140,255,.40)")
                    property("color", "#8FB8FF")
                    property("font-weight", "600")
                } else {
                    property("background", "rgba(255,255,255,.04)")
                    property("border", "1px solid rgba(255,255,255,.08)")
                    property("color", "rgba(240,244,252,.55)")
                    property("font-weight", "400")
                }
            }
            .onClick { onClick() }
    ) {
        SpanText(label, Modifier)
    }
}

@Composable
private fun RevTableHeader() {
    Row(
        Modifier.fillMaxWidth()
            .styleModifier {
                property("padding", "5px 16px")
                property("border-bottom", "1px solid rgba(255,255,255,.04)")
            }
    ) {
        // Bug-class 2 fix: min-width:0 on every flex column so long email/user values
        // can't expand a column and push subsequent columns off-screen
        // ПОЛЬЗОВАТЕЛЬ (flex 1.6)
        Box(Modifier.styleModifier { property("flex", "1.6"); property("min-width", "0") }) {
            SpanText("ПОЛЬЗОВАТЕЛЬ", Modifier.eyebrow().color(AppColors.Faint))
        }
        // ПРОВАЙДЕР (flex 1)
        Box(Modifier.styleModifier { property("flex", "1"); property("min-width", "0") }) {
            SpanText("ПРОВАЙДЕР", Modifier.eyebrow().color(AppColors.Faint))
        }
        // СТАТУС (flex 1.1)
        Box(Modifier.styleModifier { property("flex", "1.1"); property("min-width", "0") }) {
            SpanText("СТАТУС", Modifier.eyebrow().color(AppColors.Faint))
        }
        // АВТ. ПРОД. (flex 0.9)
        Box(Modifier.styleModifier { property("flex", "0.9"); property("min-width", "0") }) {
            SpanText("АВТ. ПРОД.", Modifier.eyebrow().color(AppColors.Faint))
        }
        // КУПЛЕНО (flex 1.2)
        Box(Modifier.styleModifier { property("flex", "1.2"); property("min-width", "0") }) {
            SpanText("КУПЛЕНО", Modifier.eyebrow().color(AppColors.Faint))
        }
        // ПЕРИОД ДО (flex 1.2)
        Box(Modifier.styleModifier { property("flex", "1.2"); property("min-width", "0") }) {
            SpanText("ПЕРИОД ДО", Modifier.eyebrow().color(AppColors.Faint))
        }
    }
}

@Composable
private fun RevTableRow(row: SubscriptionRow) {
    Row(
        Modifier.fillMaxWidth()
            .styleModifier {
                property("padding", "8px 16px")
                property("border-top", "1px solid rgba(255,255,255,.05)")
                property("align-items", "center")
            }
    ) {
        // Bug-class 2 fix: min-width:0 on every flex column; overflow:hidden + ellipsis
        // on text that can be long (user name, email, timestamps)
        // User column
        Box(Modifier.styleModifier { property("flex", "1.6"); property("min-width", "0") }) {
            Column(Modifier.styleModifier { property("min-width", "0") }) {
                SpanText(
                    row.user,
                    Modifier.styleModifier {
                        property("font-size", "12.5px")
                        property("font-weight", "600")
                        property("color", "#F0F4FC")
                        property("font-family", "'Inter', sans-serif")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                        property("display", "block")
                    }
                )
                SpanText(
                    row.email,
                    Modifier.styleModifier {
                        property("font-size", "11px")
                        property("color", "rgba(240,244,252,.45)")
                        property("font-family", "'Inter', sans-serif")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                        property("display", "block")
                    }
                )
            }
        }
        // Provider
        Box(Modifier.styleModifier { property("flex", "1"); property("min-width", "0") }) {
            RevProviderPill(row.provider)
        }
        // Status
        Box(Modifier.styleModifier { property("flex", "1.1"); property("min-width", "0") }) {
            Pill(revSubStatusLabel(row.status), revPillTone(row))
        }
        // Auto-renew
        Box(Modifier.styleModifier { property("flex", "0.9"); property("min-width", "0") }) {
            val on = !row.cancelAtPeriodEnd && row.status.uppercase() != "EXPIRED"
            SpanText(
                if (on) "Вкл" else "Выкл",
                Modifier.bodySMedium().styleModifier {
                    property("color", if (on) "#00E5A4" else "rgba(240,244,252,.45)")
                }
            )
        }
        // Purchased at
        Box(Modifier.styleModifier { property("flex", "1.2"); property("min-width", "0") }) {
            SpanText(
                formatTimestampShort(row.purchasedAt),
                Modifier.styleModifier {
                    property("font-size", "11.5px")
                    property("color", "rgba(240,244,252,.55)")
                    property("font-family", "'Inter', sans-serif")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                    property("display", "block")
                }
            )
        }
        // Period end
        Box(Modifier.styleModifier { property("flex", "1.2"); property("min-width", "0") }) {
            SpanText(
                formatTimestampShort(row.currentPeriodEnd),
                Modifier.styleModifier {
                    property("font-size", "11.5px")
                    property("color", "rgba(240,244,252,.55)")
                    property("font-family", "'Inter', sans-serif")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                    property("display", "block")
                }
            )
        }
    }
}

// ── Label & tone helpers ──────────────────────────────────────────────────────

@Composable
private fun RevProviderPill(provider: String) {
    val tone = when (provider.uppercase()) {
        "MONO"        -> PillTone.Accent
        "APPLE"       -> PillTone.Neutral
        "GOOGLE"      -> PillTone.Success
        "ADMIN", "ADMIN_GRANT", "ADMIN-GRANT" -> PillTone.Pro
        else          -> PillTone.Neutral
    }
    Pill(revProviderLabel(provider), tone)
}

private fun revProviderLabel(p: String): String = when (p.uppercase()) {
    "MONO"        -> "Web · mono"
    "APPLE"       -> "App Store"
    "GOOGLE"      -> "Google Play"
    "ADMIN", "ADMIN_GRANT", "ADMIN-GRANT" -> "Выдано админом"
    else          -> p
}

private fun revSubStatusLabel(s: String): String = when (s.uppercase()) {
    "ACTIVE"   -> "Активен"
    "CANCELED" -> "Отменён"
    "EXPIRED"  -> "Истёк"
    else       -> s.lowercase().replaceFirstChar { it.uppercase() }
}

private fun revPillTone(row: SubscriptionRow): PillTone = when {
    row.status.uppercase() == "ACTIVE"                           -> PillTone.Success
    row.status.uppercase() == "CANCELED" && row.active          -> PillTone.Warning
    row.status.uppercase() == "CANCELED"                         -> PillTone.Neutral
    else                                                          -> PillTone.Danger
}
